import React, { useCallback, useEffect, useMemo, useState } from "react";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import { formatApiErrorInlineLines } from "../../../utils/apiErrorDisplay";
import {
  Box,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Button as ShadcnButton } from "@/components/ui/button";
import { toast } from "sonner";
import { PineScriptCodeBlock } from "../../../components/Reports/PineScriptCodeBlock";
import { getReportStrategyDetail } from "../../../services/reportsApi";
import type { ExitPlacementComposePreviewResponse, SimulateLegs } from "../../../types/managementExplorer";
import type { ExitPlacementComposeResponse } from "../../../types/managementExplorer";
import { formatSimulatedLegsBadge } from "../../../utils/exitPlacementSimLegs";
import {
  exitPlacementComposeBlockers,
  exitPlacementPreviewSegmentSummary,
  isExitPlacementComposeReady,
  loadExitPlacementComposePreview,
  resolveRegimeEntryPolicyVersionIds,
  runExitPlacementCompose,
} from "../../../utils/exitPlacementCompose";
import { getExitHookReadinessDisplay } from "../../../utils/pineIrUi";
import { policyVersionDisplayName } from "../../../utils/reportStrategyPineUi";
import { useActiveBrokerAccount } from "../../../hooks/useActiveBrokerAccount";
import { useInstrumentSetup } from "../../../contexts/InstrumentSetupContext";
import { handleMarketDataUnresolvedError } from "../../../utils/handleMarketDataUnresolved";
import { SimulatorAlert } from "./SimulatorAlert";

const ModalAny = Modal as unknown as React.FC<any>;
const ModalOverlayAny = ModalOverlay as unknown as React.FC<any>;
const ModalContentAny = ModalContent as unknown as React.FC<any>;
const ModalHeaderAny = ModalHeader as unknown as React.FC<any>;
const ModalCloseButtonAny = ModalCloseButton as unknown as React.FC<any>;
const ModalBodyAny = ModalBody as unknown as React.FC<any>;
const ModalFooterAny = ModalFooter as unknown as React.FC<any>;

type ExitPlacementComposeDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  strategyId: string;
  sliceIds: string[];
  simulateLegs: SimulateLegs;
  runLabel?: string;
  subtleText: string;
  onComposeSuccess?: () => void;
};

function readinessBadgeColor(readiness?: string): string {
  if (readiness === "ready") return "green.500";
  if (readiness === "partial") return "orange.400";
  return "red.400";
}

function boolBadge(ok: boolean | undefined, okLabel: string, failLabel: string): string {
  if (ok === true) return okLabel;
  if (ok === false) return failLabel;
  return "—";
}

export function ExitPlacementComposeDialog({
  isOpen,
  onClose,
  reportId,
  strategyId,
  sliceIds,
  simulateLegs,
  runLabel,
  subtleText,
  onComposeSuccess,
}: ExitPlacementComposeDialogProps): React.ReactElement {
  const { activeAccountCode } = useActiveBrokerAccount();
  const { openSetup } = useInstrumentSetup();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ExitPlacementComposePreviewResponse | null>(null);
  const [entryPolicyVersionIds, setEntryPolicyVersionIds] = useState<string[]>([]);
  const [regimeVersionLabels, setRegimeVersionLabels] = useState<string[]>([]);
  const [composing, setComposing] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);
  const [composeResult, setComposeResult] = useState<ExitPlacementComposeResponse | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setPreview(null);
      setPreviewError(null);
      setComposeResult(null);
      setComposeError(null);
      setEntryPolicyVersionIds([]);
      setRegimeVersionLabels([]);
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);

    void (async () => {
      try {
        const detail = await getReportStrategyDetail(strategyId);
        if (cancelled) return;

        const versions = Array.isArray(detail.policy_versions) ? detail.policy_versions : [];
        const regimeIds = resolveRegimeEntryPolicyVersionIds(versions, {
          currentPolicyVersionId: detail.current_policy_version_id,
          lastComposePolicyVersionIds: detail.active_pine?.last_compose_policy_version_ids,
        });
        setEntryPolicyVersionIds(regimeIds);
        setRegimeVersionLabels(
          regimeIds
            .map((id) => {
              const v = versions.find((row) => row.id === id);
              return v ? policyVersionDisplayName(v) : id;
            })
            .filter(Boolean)
        );

        const previewResp = await loadExitPlacementComposePreview({
          reportId,
          sliceIds,
          strategyId,
          simulateLegs,
          entryPolicyVersionIds: regimeIds.length ? regimeIds : undefined,
        });
        if (cancelled) return;
        setPreview(previewResp);
      } catch (e) {
        if (cancelled) return;
        if (handleMarketDataUnresolvedError(e, openSetup, activeAccountCode)) {
          setPreview(null);
          setPreviewError(null);
          return;
        }
        setPreview(null);
        setPreviewError(e instanceof Error ? e.message : "Failed to load compose preview.");
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, reportId, simulateLegs, sliceIds, strategyId, activeAccountCode, openSetup]);

  const blockers = useMemo(() => exitPlacementComposeBlockers(preview), [preview]);
  const ready = isExitPlacementComposeReady(preview);
  const segmentSummary = exitPlacementPreviewSegmentSummary(preview);
  const exitHookUx = getExitHookReadinessDisplay(preview?.exit_hook_readiness);
  const composedText = composeResult?.composed_pine_source ?? composeResult?.pinescript ?? "";
  const legsBadge = formatSimulatedLegsBadge(
    simulateLegs === "both" ? ["sl", "tp"] : simulateLegs === "tp_only" ? ["tp"] : ["sl"]
  );

  const handleCompose = useCallback(async () => {
    if (!ready) {
      toast.warning("Compose not ready", {
        description: blockers[0] ?? "Fix preview issues before composing.",
      });
      return;
    }

    setComposing(true);
    setComposeError(null);
    try {
      const result = await runExitPlacementCompose({
        reportId,
        sliceIds,
        strategyId,
        simulateLegs,
        entryPolicyVersionIds: entryPolicyVersionIds.length ? entryPolicyVersionIds : undefined,
        outputMode: "merge_into_strategy",
        persistPolicyVersion: true,
      });
      setComposeResult(result);
      toast.success("Pinescript composed", {
        description: entryPolicyVersionIds.length
          ? "Merged regime entry gates and exit management into your strategy script."
          : "Merged exit management into your strategy script.",
      });
      await onComposeSuccess?.();
    } catch (e) {
      setComposeResult(null);
      if (handleMarketDataUnresolvedError(e, openSetup, activeAccountCode)) {
        setComposeError(null);
        return;
      }
      const { primary, secondary } = formatApiErrorInlineLines(e, "Compose failed.");
      setComposeError(secondary ? `${primary}\n\n${secondary}` : primary);
      presentMutationError(e, "Compose failed");
    } finally {
      setComposing(false);
    }
  }, [
    blockers,
    entryPolicyVersionIds,
    activeAccountCode,
    onComposeSuccess,
    openSetup,
    ready,
    reportId,
    simulateLegs,
    sliceIds,
    strategyId,
  ]);

  return (
    <ModalAny isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlayAny />
      <ModalContentAny maxH="90vh">
        <ModalHeaderAny fontFamily="'Urbanist', sans-serif" fontWeight="normal" color="#38BBD1">
          Compose exit management to TradingView
        </ModalHeaderAny>
        <ModalCloseButtonAny />
        <ModalBodyAny>
          <VStack align="stretch" spacing={4}>
            {runLabel?.trim() ? (
              <Text fontSize="sm" color={subtleText} fontFamily="'IBM Plex Sans', sans-serif">
                Simulation: <strong>{runLabel.trim()}</strong>
              </Text>
            ) : null}
            <Text fontSize="sm" color={subtleText} fontFamily="'IBM Plex Sans', sans-serif">
              {sliceIds.length} slice{sliceIds.length === 1 ? "" : "s"}
              {legsBadge ? ` · ${legsBadge.replace(/^Simulated:\s*/, "Export: ")}` : ""}
              {regimeVersionLabels.length
                ? ` · Regime: ${regimeVersionLabels.join(", ")}`
                : " · Exit management only"}
            </Text>

            {previewLoading ? (
              <HStack py={6} justify="center" spacing={3}>
                <Spinner size="sm" />
                <Text fontSize="sm" color={subtleText}>
                  Checking compose readiness…
                </Text>
              </HStack>
            ) : null}

            {previewError ? (
              <SimulatorAlert tone="error">{previewError}</SimulatorAlert>
            ) : null}

            {preview && !previewLoading ? (
              <>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={3}
                  fontSize="sm"
                  fontFamily="'IBM Plex Sans', sans-serif"
                >
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between" flexWrap="wrap" gap={2}>
                      <Text>
                        Exit hooks:{" "}
                        <Text as="span" color={readinessBadgeColor(preview.exit_hook_readiness)} fontWeight="medium">
                          {exitHookUx?.shortLabel ?? preview.exit_hook_readiness ?? "—"}
                        </Text>
                      </Text>
                      <Text>
                        Management hooked:{" "}
                        <Text
                          as="span"
                          color={preview.management_hooked === false ? "red.400" : "green.500"}
                          fontWeight="medium"
                        >
                          {boolBadge(preview.management_hooked, "Yes", "No")}
                        </Text>
                      </Text>
                      <Text>
                        Combination:{" "}
                        <Text
                          as="span"
                          color={preview.combination_ok === false ? "red.400" : "green.500"}
                          fontWeight="medium"
                        >
                          {boolBadge(preview.combination_ok, "OK", "Blocked")}
                        </Text>
                      </Text>
                    </HStack>
                    {segmentSummary ? (
                      <Text color={subtleText}>Bundle segments: {segmentSummary}</Text>
                    ) : null}
                    {preview.compose_hint?.trim() ? (
                      <Text color={subtleText}>{preview.compose_hint.trim()}</Text>
                    ) : null}
                    {preview.recommended_compose_mode ? (
                      <Text color={subtleText}>
                        Recommended mode: {preview.recommended_compose_mode.replace(/_/g, " ")}
                      </Text>
                    ) : null}
                  </VStack>
                </Box>

                {!ready && blockers.length ? (
                  <SimulatorAlert tone="warning">
                    <div className="flex flex-col gap-1">
                      {blockers.map((msg) => (
                        <p key={msg}>{msg}</p>
                      ))}
                    </div>
                  </SimulatorAlert>
                ) : null}

                {ready ? (
                  <SimulatorAlert tone="success">
                    Ready to merge into strategy. Your Pinescript must use named{" "}
                    <code className="text-xs">stop=</code> / <code className="text-xs">limit=</code> on{" "}
                    <code className="text-xs">strategy.exit()</code> for live SL/TP hooks.
                  </SimulatorAlert>
                ) : null}
              </>
            ) : null}

            {composeError ? (
              <SimulatorAlert tone="error">{composeError}</SimulatorAlert>
            ) : null}

            {composedText ? (
              <Box>
                <Text fontSize="xs" color={subtleText} mb={2} textTransform="uppercase">
                  Composed Pinescript
                </Text>
                <PineScriptCodeBlock code={composedText} maxH="min(40vh, 360px)" />
              </Box>
            ) : null}
          </VStack>
        </ModalBodyAny>
        <ModalFooterAny gap={2}>
          <ShadcnButton type="button" variant="outline" size="sm" onClick={onClose}>
            {composedText ? "Close" : "Cancel"}
          </ShadcnButton>
          {!composedText ? (
            <ShadcnButton
              type="button"
              variant="gradient"
              size="sm"
              disabled={previewLoading || composing || !ready}
              onClick={() => void handleCompose()}
            >
              {composing ? "Composing…" : "Merge into strategy"}
            </ShadcnButton>
          ) : null}
        </ModalFooterAny>
      </ModalContentAny>
    </ModalAny>
  );
}
